/**
 * Status-Automat eines aTM-Vorgangs (ARCHITEKTUR §3).
 * Einzige Quelle für erlaubte Übergänge – Server, UI und Tests nutzen diese Tabelle.
 */

export const CASE_STATUSES = [
  "angelegt",
  "triagiert",
  "wartend",
  "angenommen",
  "in_behandlung",
  "abgeschlossen",
  "abgebrochen",
] as const;
export type CaseStatus = (typeof CASE_STATUSES)[number];

export const TERMINAL_STATUSES: ReadonlySet<CaseStatus> = new Set(["abgeschlossen", "abgebrochen"]);

/** Zustände, in denen ein Arzt den Fall führt (zählen gegen MAX_ACTIVE_CASES_PER_DOCTOR). */
export const DOCTOR_ACTIVE_STATUSES: ReadonlySet<CaseStatus> = new Set(["angenommen", "in_behandlung"]);

export const CANCEL_REASONS = [
  "notfall",
  "nicht_geeignet",
  "apotheke_abbruch",
  "patient_gegangen",
  "arzt_abbruch",
  "technischer_fehler",
  "entwurf_verworfen",
] as const;
export type CancelReason = (typeof CANCEL_REASONS)[number];

export type TransitionRole = "apotheke" | "arzt" | "system";

export const CASE_ACTIONS = [
  "triage_geeignet",
  "triage_ausschluss",
  "einreihen",
  "zurueckziehen",
  "annehmen",
  "zurueckgeben",
  "beitritt",
  "abschliessen",
  "abbrechen",
  "entwurf_verwerfen",
] as const;
export type CaseAction = (typeof CASE_ACTIONS)[number];

export interface TransitionRule {
  /** Nummer aus ARCHITEKTUR §3.2 */
  id: string;
  action: CaseAction;
  from: readonly CaseStatus[];
  to: CaseStatus;
  roles: readonly TransitionRole[];
}

export const TRANSITIONS: readonly TransitionRule[] = [
  { id: "T3", action: "triage_geeignet", from: ["angelegt"], to: "triagiert", roles: ["apotheke", "system"] },
  {
    id: "T4",
    action: "triage_ausschluss",
    from: ["angelegt"],
    to: "abgebrochen",
    roles: ["apotheke", "system"],
  },
  { id: "T5", action: "einreihen", from: ["triagiert"], to: "wartend", roles: ["apotheke"] },
  { id: "T6", action: "zurueckziehen", from: ["wartend"], to: "triagiert", roles: ["apotheke"] },
  { id: "T7", action: "annehmen", from: ["wartend"], to: "angenommen", roles: ["arzt"] },
  { id: "T8", action: "zurueckgeben", from: ["angenommen"], to: "wartend", roles: ["arzt"] },
  {
    id: "T9",
    action: "beitritt",
    from: ["angenommen"],
    to: "in_behandlung",
    roles: ["apotheke", "arzt", "system"],
  },
  {
    id: "T10",
    action: "abschliessen",
    from: ["angenommen", "in_behandlung"],
    to: "abgeschlossen",
    roles: ["arzt"],
  },
  {
    id: "T11",
    action: "abbrechen",
    from: ["angelegt", "triagiert", "wartend"],
    to: "abgebrochen",
    roles: ["apotheke"],
  },
  {
    id: "T12",
    action: "abbrechen",
    from: ["angenommen", "in_behandlung"],
    to: "abgebrochen",
    roles: ["apotheke", "arzt"],
  },
  { id: "T13", action: "entwurf_verwerfen", from: ["angelegt"], to: "abgebrochen", roles: ["system"] },
];

export class TransitionError extends Error {
  readonly code = "transition_not_allowed";
  constructor(
    readonly action: CaseAction,
    readonly from: CaseStatus,
    readonly role: TransitionRole,
  ) {
    super(`Übergang ${action} aus ${from} für Rolle ${role} nicht erlaubt`);
    this.name = "TransitionError";
  }
}

export function findTransition(
  action: CaseAction,
  from: CaseStatus,
  role: TransitionRole,
): TransitionRule | undefined {
  return TRANSITIONS.find((t) => t.action === action && t.from.includes(from) && t.roles.includes(role));
}

/** Liefert den Zielzustand oder wirft TransitionError. */
export function transition(action: CaseAction, from: CaseStatus, role: TransitionRole): CaseStatus {
  const rule = findTransition(action, from, role);
  if (!rule) throw new TransitionError(action, from, role);
  return rule.to;
}

/** Aktionen, die eine Rolle im Zustand `from` auslösen darf (z. B. für Button-Sichtbarkeit). */
export function allowedActions(from: CaseStatus, role: TransitionRole): CaseAction[] {
  const out = new Set<CaseAction>();
  for (const t of TRANSITIONS) if (t.from.includes(from) && t.roles.includes(role)) out.add(t.action);
  return [...out];
}

/** Abbruchgründe, die eine Rolle im jeweiligen Zustand angeben darf. */
export function allowedCancelReasons(from: CaseStatus, role: TransitionRole): CancelReason[] {
  if (!findTransition("abbrechen", from, role) && !findTransition("triage_ausschluss", from, role)) return [];
  if (role === "arzt") return ["arzt_abbruch", "patient_gegangen", "technischer_fehler"];
  if (from === "angenommen" || from === "in_behandlung")
    return ["patient_gegangen", "apotheke_abbruch", "technischer_fehler"];
  return ["apotheke_abbruch", "patient_gegangen", "technischer_fehler"];
}
